import { DEFAULT_WORK_TIME_ZONE, wallTimeToInstant } from '@rendimees24/shared-types';
/** Shared calendar for the current EE worker flow; never use the device timezone. */
export const WORKER_CALENDAR_ZONE = DEFAULT_WORK_TIME_ZONE;
const parts = (date: Date, zone = WORKER_CALENDAR_ZONE) =>
  Object.fromEntries(
    new Intl.DateTimeFormat('en-GB', {
      timeZone: zone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    })
      .formatToParts(date)
      .map((p) => [p.type, p.value]),
  );
export function workerCalendarDay(date: Date, zone = WORKER_CALENDAR_ZONE): string {
  const p = parts(date, zone);
  return `${p.year}-${p.month}-${p.day}`;
}
/** Date-only and wall-clock values are already calendar values, not UTC instants. */
export function workerDate(value?: string, zone = WORKER_CALENDAR_ZONE): string {
  if (!value || !Number.isFinite(Date.parse(value))) return '';
  return /(?:Z|[+-]\d{2}:?\d{2})$/i.test(value)
    ? workerCalendarDay(new Date(value), zone)
    : value.slice(0, 10);
}
export function formatWorkerDate(value?: string, zone = WORKER_CALENDAR_ZONE): string {
  const date = workerDate(value, zone);
  if (!date) return '—';
  return `${date.slice(8, 10)}.${date.slice(5, 7)}.${date.slice(0, 4)}`;
}
export function workerTimeInput(iso?: string, zone = WORKER_CALENDAR_ZONE): string {
  if (!iso || !Number.isFinite(Date.parse(iso))) return '';
  const p = parts(new Date(iso), zone);
  return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}`;
}
export function formatWorkerTime(iso?: string, zone = WORKER_CALENDAR_ZONE): string {
  const input = workerTimeInput(iso, zone);
  return input ? `${formatWorkerDate(input.slice(0, 10))} ${input.slice(11)}` : '—';
}
/** Round-trip the wall-clock input. Reject nonexistent/ambiguous DST times rather than guess. */
export function parseWorkerTime(
  value: string,
  zone = WORKER_CALENDAR_ZONE,
  original?: string,
): string | undefined {
  // Keep seconds and the selected autumn occurrence when the wall time is unchanged.
  if (original && workerTimeInput(original, zone) === value)
    return new Date(original).toISOString();
  return wallTimeToInstant(value, zone);
}
